import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Box } from '@livable/saas-react-components';
import { Container, Tab, Tabs } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import BreadcrumbsBar from 'src/components/general/BreadcrumbsBar';
import { PropertyInformationPage } from 'src/components/property/PropertyInformationPage';
import { PropertyContactInformation } from 'src/components/property/PropertyContactInformation';
import { PropertyFeeStructurePageV2 } from 'src/components/property/PropertyFeeStructurePageV2';
import { PropertyUtilitiesSetupPageV2 } from 'src/components/property/PropertyUtilitiesSetupPageV2';
import { PropertyResidentsAndFeeSetup } from 'src/components/property/PropertyResidentsAndFeeSetup';
import { PropertyUnitSetupPage } from 'src/components/property/PropertyUnitSetupPage';
import { PropertyUtilityAccountsPage } from 'src/pages/PropertyUtilityAccountsPage';
import { useStore } from 'src/stores';
import { PropertyPayoutDetailsPage } from 'src/components/property/PropertyPayoutDetailsPage';
import { useURLHandler } from 'src/utils/hooks';
import usePropertyEditPageStyles from './styles';
export const PropertyEditPage = observer(() => {
  const classes = usePropertyEditPageStyles();
  const location = useLocation();
  const store = useStore();
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  const urlParams = new URLSearchParams(location.search);
  const propertyId = urlParams.get('id');

  // Read once and used for both the tab and its URL name below, so the two cannot disagree.
  const isBillsEnabled = store.account.featureFlagsStore.getIsBillsDashboardEnabled;

  // useURLHandler treats this as the set of valid tab values, so a name missing from it makes
  // ?tab=... and a refresh fall back to 'information' -- which is why 'bill-routing' has to be
  // here and not only in tabs, and why it is conditional on the same flag.
  //
  // This list is not a full mirror of the rendered tabs: 'contact-info' and
  // 'reimbursement-details' are unconditional here while their tabs are only spliced in outside
  // demo mode, so in demo mode ?tab=contact-info resolves to a tab that is not rendered. That
  // predates this change and is left alone deliberately -- see LB-5804 -- rather than altering
  // demo-mode behaviour in a release-safety PR.
  const tabsNames = ['information', 'fee-structure', 'utilities', 'unit', 'contact-info', 'reimbursement-details', ...(isBillsEnabled ? ['bill-routing'] : [])];
  const _useURLHandler = _objectSpread({}, useURLHandler('information', tabsNames, {
      id: propertyId
    })),
    handleSelectedTabChange = _useURLHandler.handleSelectedTabChange,
    selectedTab = _useURLHandler.selectedTab;
  const breadcrumbArray = [{
    title: t('general.dashboard'),
    link: '/active_dashboard'
  }, {
    link: "/residencies?id=".concat(propertyId, "&tab=residencies"),
    title: store.addNewProperty.propertyInformation.propertyAddress
  }, {
    title: t('general.edit_property')
  }];
  const tabs = [{
    label: t('add_new_property_flow.property_information'),
    value: 'information',
    component: /*#__PURE__*/React.createElement(PropertyInformationPage, {
      editMode: true,
      mutablePropertyId: propertyId
    })
  }, {
    label: t('add_new_property_flow.fee_structure'),
    value: 'fee-structure',
    component: store.company.companyFeatureStore.isAppfolio ? /*#__PURE__*/React.createElement(PropertyResidentsAndFeeSetup, {
      editMode: true,
      mutablePropertyId: propertyId
    }) : /*#__PURE__*/React.createElement(PropertyFeeStructurePageV2, {
      editMode: true,
      mutablePropertyId: propertyId
    })
  }, {
    label: t('add_new_property_flow.utilities_setup'),
    value: 'utilities',
    component: /*#__PURE__*/React.createElement(PropertyUtilitiesSetupPageV2, {
      editMode: true,
      mutablePropertyId: propertyId
    })
  }, {
    label: t('add_new_property_flow.unit_setup'),
    value: 'unit',
    component: /*#__PURE__*/React.createElement(PropertyUnitSetupPage, {
      editMode: true,
      mutablePropertyId: propertyId
    })
  }];

  // Bill routing only means anything once bills can arrive, so it follows the same flag as the
  // Bills dashboard rather than standing on its own. Appended rather than declared inline above so
  // it keeps its position as the last tab when present.
  if (isBillsEnabled) {
    tabs.push({
      // Its own tab rather than a section inside Utilities setup: that page is a multi-step form
      // driven by its own store, and this is unrelated data with a different lifecycle.
      label: t('add_new_property_flow.bill_routing'),
      value: 'bill-routing',
      component: /*#__PURE__*/React.createElement(PropertyUtilityAccountsPage, {
        mutablePropertyId: propertyId
      })
    });
  }
  if (localStorage.getItem('livable_demo_mode') !== 'Demo') {
    tabs.splice(1, 0, {
      label: t('add_new_property_flow.contact_information'),
      value: 'contact-info',
      component: /*#__PURE__*/React.createElement(PropertyContactInformation, {
        editMode: true,
        mutablePropertyId: propertyId
      })
    });
    tabs.splice(3, 0, {
      label: t('subscriptions.payment_information'),
      value: 'reimbursement-details',
      component: /*#__PURE__*/React.createElement(PropertyPayoutDetailsPage, {
        editMode: true,
        mutablePropertyId: propertyId
      })
    });
  }
  const handleActiveTabChange = (event, newValue) => {
    if (selectedTab === 'utilities') store.addNewProperty.utilitiesSetupV2.resetStoreToInitValues();
    handleSelectedTabChange(newValue);
  };
  const getCurrentComponent = () => {
    var _tabs$find;
    return (_tabs$find = tabs.find(tab => tab.value === selectedTab)) === null || _tabs$find === void 0 ? void 0 : _tabs$find.component;
  };
  useEffect(() => {
    const getProperty = data => store.properties.getPropertyAsync.run(data);
    if (store.account.featureFlagsStore.getIsNewPayoutEnabled) {
      store.portfolios.getPortfoliosDashboardAsync.run({
        storeCompany: true
      }, [propertyId]);
    }
    getProperty({
      propertyId
    });
    return () => {
      store.addNewProperty.resetStoreToInitValues();
      store.vendors.resetStore();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(BreadcrumbsBar, {
    breadcrumbsArray: breadcrumbArray
  }), /*#__PURE__*/React.createElement(Tabs, {
    type: "navigation",
    value: selectedTab,
    onChange: handleActiveTabChange,
    variant: "scrollable",
    scrollButtons: "auto",
    "aria-label": "edit property page tabs",
    className: classes.tabs,
    allowScrollButtonsMobile: true
  }, tabs.map(tab => /*#__PURE__*/React.createElement(Tab, {
    value: tab.value,
    label: tab.label,
    key: tab.value,
    disabled: store.units.getUnitsAsync.inProgress || store.account.getProfileAsync.inProgress || store.fees.getExistingFeesAsync.inProgress || store.buildings.getBuildingAsync.inProgress || store.properties.getPropertyAsync.inProgress || store.properties.getPropertyFeesAsync.inProgress || store.properties.getPropertysContactsAsync.inProgress || store.portfolios.getExistingPortfoliosAsync.inProgress || store.teamMembers.getCompanyTeamMembersAsync.inProgress || store.payment.getPayoutAchDirectDepositAsync.inProgress || store.inUnitAmenities.getAllInUnitAmenitiesAsync.inProgress || store.amenities.getAllCommonAreaAmenitiesRawAsync.inProgress || store.properties.getPropertyUtilityProvidersV3Async.inProgress || store.utilityProviders.getPropertyCommonAreaAmenitiesAsync.inProgress,
    'custom-attribute': '751-Property-edit-page-' + tab.value
  }))), /*#__PURE__*/React.createElement(Container, {
    variant: "narrow"
  }, /*#__PURE__*/React.createElement(Box, null, getCurrentComponent())));
});